import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeviceActionDialog from "./DeviceActionDialog";
const formatPulseDate = (value) => {
  if (!value) return "Sin información";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Sin información"
    : date.toLocaleString("es-CO", { timeZone: "America/Bogota" });
};
export default function SendPulseDialog({
  open,
  onClose,
  onConfirm,
  antenna,
  reader,
  sendingPulse = false,
}) {
  const [customDuration, setCustomDuration] = useState(false);
  const [duration, setDuration] = useState("1000");
  useEffect(() => {
    if (!open) {
      setCustomDuration(false);
      setDuration("1000");
    }
  }, [open]);
  const invalid =
    customDuration && (!/^\d+$/.test(duration) || Number(duration) < 1 || Number(duration) > 10000);
  return (
    <DeviceActionDialog
      open={open}
      onClose={onClose}
      onConfirm={() => onConfirm(antenna.id, customDuration ? Number(duration) : null)}
      title="Enviar pulso"
      maxWidth="md"
      fieldColumns={4}
      renderResult={(result) => (
        <Box sx={{ mt: 1.5, overflowWrap: "anywhere" }}>
          <Typography variant="body2">
            <strong>Antena:</strong> {result.antennaId ?? antenna?.id}
          </Typography>
          {result.resetDate && (
            <Typography variant="body2">
              <strong>Fecha del pulso:</strong> {formatPulseDate(result.resetDate)}
            </Typography>
          )}
          {result.requestedDuration != null && (
            <Typography variant="body2">
              <strong>Duración:</strong> {result.requestedDuration} ms
            </Typography>
          )}
          {result.resetResponse && (
            <Typography variant="body2">
              <strong>Respuesta del dispositivo:</strong> {result.resetResponse}
            </Typography>
          )}
        </Box>
      )}
      subtitle={antenna?.name || antenna?.rawData?.namepoint || antenna?.rawData?.point}
      actionLabel="Enviar pulso"
      loading={sendingPulse}
      disabled={!antenna?.id || reader?.isOnCooldown || invalid}
      fields={[
        ["Antena", antenna?.rawData?.namepoint || antenna?.name || antenna?.rawData?.point],
        ["ID antena", antenna?.id],
        ["ID host", reader?.hostId ?? antenna?.rawData?.idhost],
        ["Punto de lectura", antenna?.rawData?.point],
        ["Estado registrado", antenna?.status || "Sin verificar"],
        ["Reader", reader?.hostname],
        ["IP", reader?.ip],
        ["Tipo de reader", reader?.reader_type],
        ["Comercio", reader?.commerce],
      ]}
    >
      <Box
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid #dfe6ed",
          bgcolor: "#fff",
          borderRadius: 1.5,
          overflowWrap: "anywhere",
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#243b53", mb: 1 }}>
          Último pulso registrado
        </Typography>
        <Typography variant="body2">
          <strong>Fecha:</strong>{" "}
          {formatPulseDate(antenna?.lastPulse || antenna?.rawData?.more?.resetdate)}
        </Typography>
        <Typography variant="body2">
          <strong>Respuesta:</strong> {antenna?.rawData?.more?.resetresponse || "Sin información"}
        </Typography>
      </Box>
      <Alert severity={reader?.isOnCooldown ? "info" : "warning"} sx={{ mb: 2 }}>
        {reader?.isOnCooldown
          ? "El reader está en tiempo de espera después de un reinicio."
          : "Confirma el envío de un pulso a esta antena. Puede accionar el dispositivo de acceso asociado."}
      </Alert>
      <FormControlLabel
        control={
          <Switch
            checked={customDuration}
            onChange={(event) => setCustomDuration(event.target.checked)}
          />
        }
        label="Configurar duración del pulso"
      />
      {customDuration && (
        <Box>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Duración (ms)"
            value={duration}
            onChange={(event) => setDuration(event.target.value)}
            error={invalid}
            helperText={
              invalid
                ? "Ingresa un número entero entre 1 y 10000 ms."
                : "Duración enviada a la antena."
            }
            inputProps={{ min: 1, max: 10000, step: 1 }}
            sx={{ mt: 1.5 }}
          />
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
            {[500, 1000, 2000, 5000].map((value) => (
              <Button
                key={value}
                size="small"
                variant={duration === String(value) ? "contained" : "outlined"}
                aria-pressed={duration === String(value)}
                onClick={() => setDuration(String(value))}
                sx={{
                  minWidth: 88,
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  color: duration === String(value) ? "#fff" : "#164b65",
                  bgcolor: duration === String(value) ? "#164b65" : "#fff",
                  borderColor: "#164b65",
                  boxShadow: "none",
                  "&:hover": {
                    color: duration === String(value) ? "#fff" : "#103b50",
                    bgcolor: duration === String(value) ? "#103b50" : "#e8f1f6",
                    borderColor: "#103b50",
                    boxShadow: "none",
                  },
                  "&.Mui-focusVisible": {
                    outline: "3px solid #8db8d1",
                    outlineOffset: 2,
                  },
                }}
              >
                {value} ms
              </Button>
            ))}
          </Stack>
        </Box>
      )}
      <Typography variant="body2" sx={{ mt: 1.5, color: "#475569" }}>
        {customDuration
          ? `Se enviará un pulso de ${duration || "—"} ms.`
          : "Se utilizará la duración predeterminada del dispositivo."}
      </Typography>
    </DeviceActionDialog>
  );
}
SendPulseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  antenna: PropTypes.object,
  reader: PropTypes.object,
  sendingPulse: PropTypes.bool,
};
